/**
 * Content Approval Service
 * Manages pending content drafts created by the CrewAI agent
 */

import { db } from '../firebase';
import {
  collection,
  doc,
  getDocs,
  getDoc,
  updateDoc,
  addDoc,
  deleteDoc,
  query,
  where,
  orderBy,
  Timestamp,
} from 'firebase/firestore';

export interface PendingContent {
  id: string;
  contentType: 'website_news' | 'instagram';
  status: 'pending' | 'approved' | 'rejected' | 'published';
  createdAt: Timestamp;
  source: string;
  // Website news fields
  title?: string;
  summary?: string;
  fullContent?: string;
  category?: string;
  // Instagram fields
  imageUrl?: string;
  caption?: string;
  // Approval metadata
  approvedAt?: Timestamp;
  approvedBy?: string;
  rejectedAt?: Timestamp;
  rejectedBy?: string;
  rejectionReason?: string;
  publishedAt?: Timestamp;
  publishedId?: string;
}

const PENDING_COLLECTION = 'pendingContent';
const NEWS_COLLECTION = 'news';

/**
 * Get all pending content drafts
 */
export async function getPendingContent(): Promise<PendingContent[]> {
  const pendingRef = collection(db, PENDING_COLLECTION);
  const q = query(
    pendingRef,
    where('status', '==', 'pending'),
    orderBy('createdAt', 'desc')
  );

  const snapshot = await getDocs(q);
  return snapshot.docs.map(doc => ({
    id: doc.id,
    ...doc.data()
  } as PendingContent));
}

/**
 * Get count of pending drafts
 */
export async function getPendingContentCount(): Promise<number> {
  const items = await getPendingContent();
  return items.length;
}

/**
 * Get all content (including approved/rejected) for history
 */
export async function getAllContent(): Promise<PendingContent[]> {
  const pendingRef = collection(db, PENDING_COLLECTION);
  const q = query(pendingRef, orderBy('createdAt', 'desc'));

  const snapshot = await getDocs(q);
  return snapshot.docs.map(doc => ({
    id: doc.id,
    ...doc.data()
  } as PendingContent));
}

/**
 * Approve a content draft
 */
export async function approveContent(
  draftId: string,
  adminEmail: string
): Promise<void> {
  const draftRef = doc(db, PENDING_COLLECTION, draftId);
  await updateDoc(draftRef, {
    status: 'approved',
    approvedAt: Timestamp.now(),
    approvedBy: adminEmail,
  });
}

/**
 * Reject a content draft
 */
export async function rejectContent(
  draftId: string,
  adminEmail: string,
  reason: string = ''
): Promise<void> {
  const draftRef = doc(db, PENDING_COLLECTION, draftId);
  await updateDoc(draftRef, {
    status: 'rejected',
    rejectedAt: Timestamp.now(),
    rejectedBy: adminEmail,
    rejectionReason: reason,
  });
}

/**
 * Publish an approved news draft to the live website
 */
export async function publishNews(draftId: string): Promise<string> {
  const draftRef = doc(db, PENDING_COLLECTION, draftId);
  const draftDoc = await getDoc(draftRef);

  if (!draftDoc.exists()) {
    throw new Error('Draft not found');
  }

  const draft = draftDoc.data() as PendingContent;

  if (draft.status !== 'approved') {
    throw new Error('Draft must be approved before publishing');
  }

  if (draft.contentType !== 'website_news') {
    throw new Error('This draft is not website news');
  }

  // Add to live news collection
  const newsRef = collection(db, NEWS_COLLECTION);
  const newsDoc = await addDoc(newsRef, {
    title: draft.title,
    summary: draft.summary,
    fullContent: draft.fullContent,
    category: draft.category,
    createdAt: Timestamp.now(),
    published: true,
    source: 'agent',
  });

  // Update draft status
  await updateDoc(draftRef, {
    status: 'published',
    publishedAt: Timestamp.now(),
    publishedId: newsDoc.id,
  });

  return newsDoc.id;
}

/**
 * Update a draft's content (for editing before approval)
 */
export async function updateDraft(
  draftId: string,
  updates: Partial<PendingContent>
): Promise<void> {
  const draftRef = doc(db, PENDING_COLLECTION, draftId);
  // Remove id from updates if present
  const { id, ...safeUpdates } = updates as any;
  await updateDoc(draftRef, safeUpdates);
}

/**
 * Delete a draft
 */
export async function deleteDraft(draftId: string): Promise<void> {
  const draftRef = doc(db, PENDING_COLLECTION, draftId);
  await deleteDoc(draftRef);
}
